import { date } from "@/helpers/time";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Table as ChakraTable,
  HStack,
  Heading,
  Text,
} from "@chakra-ui/react";
import { monthlyClaimAmount } from "../reporting";
import { ToClaimItem } from "../types";

type Props = {
  items: ToClaimItem[];
  reportablePeriods: (item: ToClaimItem) => string[];
  onToggleAll: (selected: boolean) => void;
  onToggle: (contractId: string, selected: boolean) => void;
  onTogglePeriod: (contractId: string, period: string) => void;
  onRemove: (contractId: string) => void;
};

const COLUMNS = ["Inquilino", "Cánones en mora", "Monto", ""];

const ToClaimTable = ({
  items,
  reportablePeriods,
  onToggleAll,
  onToggle,
  onTogglePeriod,
  onRemove,
}: Props): JSX.Element => {
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");
  const allSelected = items.length > 0 && items.every((item) => item.selected);

  return (
    <ChakraTable.ScrollArea
      rounded="lg"
      bg="white"
      boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
    >
      <ChakraTable.Root size="sm">
        <ChakraTable.Header>
          <ChakraTable.Row bg="#fcfcfd">
            <ChakraTable.ColumnHeader width="40px">
              <Checkbox.Root
                checked={allSelected}
                disabled={items.length === 0}
                onCheckedChange={(e) => onToggleAll(!!e.checked)}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </ChakraTable.ColumnHeader>
            {COLUMNS.map((label) => (
              <ChakraTable.ColumnHeader key={label} color="gray.500">
                <Heading size="xs" fontWeight="extrabold">
                  {label}
                </Heading>
              </ChakraTable.ColumnHeader>
            ))}
          </ChakraTable.Row>
        </ChakraTable.Header>
        <ChakraTable.Body>
          {items.map((item) => {
            const { contract } = item;

            return (
              <ChakraTable.Row key={contract.id}>
                <ChakraTable.Cell>
                  <Checkbox.Root
                    checked={item.selected}
                    disabled={item.periods.length === 0}
                    onCheckedChange={(e) => onToggle(contract.id, !!e.checked)}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </ChakraTable.Cell>
                <ChakraTable.Cell>
                  <Text fontWeight={600}>{contract.tenantName}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {contract.propertyAddress || "Sin dirección"}
                  </Text>
                </ChakraTable.Cell>
                <ChakraTable.Cell>
                  <HStack gap={2}>
                    {reportablePeriods(item).map((period) => {
                      const isChecked = item.periods.includes(period);

                      return (
                        <Badge
                          key={period}
                          as="button"
                          cursor="pointer"
                          size="md"
                          bg={isChecked ? "#FFF7D6" : "#F7F7F7"}
                          color={isChecked ? "#E6AE22" : "#9F9DB0"}
                          onClick={() => onTogglePeriod(contract.id, period)}
                        >
                          {date(`${period}-01`).format("MMM")}
                          {isChecked && " ✓"}
                        </Badge>
                      );
                    })}
                  </HStack>
                </ChakraTable.Cell>
                <ChakraTable.Cell>
                  {formatCurrency(
                    monthlyClaimAmount(contract) * item.periods.length
                  )}
                </ChakraTable.Cell>
                <ChakraTable.Cell textAlign="end">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onRemove(contract.id)}
                  >
                    Quitar
                  </Button>
                </ChakraTable.Cell>
              </ChakraTable.Row>
            );
          })}
        </ChakraTable.Body>
      </ChakraTable.Root>
      {items.length === 0 && (
        <Box p={6} textAlign="center">
          <Text fontSize="sm" color="gray.600">
            Agrega los contratos con cánones en mora que quieres reclamar.
          </Text>
        </Box>
      )}
    </ChakraTable.ScrollArea>
  );
};

export default ToClaimTable;
